/**
 * Sovereign shared types.
 *
 * This file is the contract between the engine (src/engine), the game Worker (worker/src) and the client
 * (src/client). It holds only types and small constant id lists, so it runs unchanged in the browser, in the
 * Worker and under plain Node 24 (type stripping). Use relative imports only.
 *
 * Design notes
 * - The whole world is one JSON value (`WorldState`) that fits comfortably in one SQLite row (typically
 *   40-120 KB). Chat messages and the full event log live in their own tables, outside `WorldState`.
 * - All game numbers are small integers. The Heat Meter uses a x8 integer scale (display = meter / 8).
 * - `CountryState` is private. Students only ever receive a `StudentView` built by the engine's
 *   `buildView`, which applies fog of war. Never send a raw `WorldState` or `CountryState` to a student.
 * - Real-world "inspired by" labels are teacher-only and live in engine data, never in this state.
 */

/* ------------------------------------------------------------------------------------------------ */
/* Ids                                                                                              */
/* ------------------------------------------------------------------------------------------------ */

/** The eight ideology countries. A country's id is its ideology id (each ideology appears once per world). */
export const IDEOLOGY_IDS = [
  'ironvale',
  'brightwater',
  'meridia',
  'kestran',
  'vellmark',
  'alvera',
  'karsova',
  'aurem',
] as const;
export type IdeologyId = (typeof IDEOLOGY_IDS)[number];
export type CountryId = IdeologyId;

/** The "Core Four" ideologies that are always human-played in the default placement mode. */
export const CORE_FOUR: readonly CountryId[] = ['ironvale', 'brightwater', 'meridia', 'kestran'];
/** The other four ideologies; the lowest-cost subset of these fills the remaining human seats. */
export const OTHER_FOUR: readonly CountryId[] = ['vellmark', 'alvera', 'karsova', 'aurem'];

/** The eight geography slots. The first four are high emitters; the last four are Most Vulnerable. */
export const SLOT_IDS = ['harbor', 'heartland', 'sunbelt', 'boreal', 'atoll', 'delta', 'highland', 'canopy'] as const;
export type SlotId = (typeof SLOT_IDS)[number];

/** Cabinet roles. "foreign" includes the UN Ambassador duties. */
export const ROLE_IDS = ['head', 'foreign', 'defense', 'finance', 'intel', 'interior'] as const;
export type RoleId = (typeof ROLE_IDS)[number];

/** The five quiz axes. Each value is in [-1, 1]. */
export const AXES = ['economy', 'liberty_order', 'tradition_progress', 'sovereignty_global', 'growth_green'] as const;
export type Axis = (typeof AXES)[number];
export type AxisVector = Record<Axis, number>;

export const TEMPERAMENTS = ['trader', 'guardian', 'opportunist', 'isolationist', 'broker'] as const;
export type Temperament = (typeof TEMPERAMENTS)[number];

export const RESOURCES = ['treasury', 'energy', 'food'] as const;
export type Resource = (typeof RESOURCES)[number];

export const INDEX_KEYS = ['prosperity', 'security', 'wellbeing', 'environment'] as const;
export type IndexKey = (typeof INDEX_KEYS)[number];

export const UNIT_TYPES = ['army', 'navy', 'air', 'defenses'] as const;
export type UnitType = (typeof UNIT_TYPES)[number];

export const POSTURES = ['defensive', 'ready', 'alert'] as const;
export type Posture = (typeof POSTURES)[number];

export const PROGRAM_IDS = [
  'health_service',
  'schools',
  'infrastructure',
  'farm_support',
  'energy_grid',
  'public_safety',
  'civic_inclusion',
  'community_charity',
  'civic_association',
  'workforce_renewal',
  'family_skills',
  'workforce_training',
  'diversification',
] as const;
export type ProgramId = (typeof PROGRAM_IDS)[number];

export const CLIMATE_ACTION_IDS = [
  'green_transition',
  'double_green',
  'energy_innovation',
  'expand_fossil',
  'resilience',
  'early_warning',
  'heat_safe',
  'mangrove',
  'conservation',
  'clean_tech_green',
  'clean_tech_resilience',
  'innovation_prize',
  'forest_clearing',
] as const;
export type ClimateActionId = (typeof CLIMATE_ACTION_IDS)[number];

export const MISSION_IDS = ['gather', 'counterintel', 'sabotage', 'propaganda', 'steal_tech'] as const;
export type MissionId = (typeof MISSION_IDS)[number];

export const TREATY_TYPES = [
  'non_aggression',
  'alliance',
  'trade',
  'embassy',
  'aid',
  'water',
  'mutual_aid',
  'relocation',
  'human_rights',
  'peace',
  'free_trade_zone',
  'custom',
] as const;
export type TreatyType = (typeof TREATY_TYPES)[number];

export const CRISIS_IDS = ['pirates', 'drought', 'pandemic', 'island', 'refugees', 'flooding', 'terror'] as const;
export type CrisisId = (typeof CRISIS_IDS)[number];

export const PLEDGE_ROUTES = [
  'fund_pledge',
  'clean_tech_transfer',
  'green_development_loan',
  'neighbor_adaptation_aid',
  'sovereign_fund_grant',
  'innovation_prize',
  'solidarity_aid',
] as const;
export type PledgeRoute = (typeof PLEDGE_ROUTES)[number];

export const PAYOUT_FORMULAS = ['equal', 'damage', 'vulnerability'] as const;
export type PayoutFormula = (typeof PAYOUT_FORMULAS)[number];

export type StudentId = string;
export type ProposalId = string;
export type TreatyId = string;
export type NewsId = string;
export type AssemblyItemId = string;
/** Land zones are `${CountryId}-1` (capital), `${CountryId}-2`, `${CountryId}-3`. The T12 island is `island`. */
export type ZoneId = string;

/* ------------------------------------------------------------------------------------------------ */
/* Small value types                                                                                */
/* ------------------------------------------------------------------------------------------------ */

export interface ResourceBundle {
  treasury: number;
  energy: number;
  food: number;
}
export type IndexValues = Record<IndexKey, number>;
export type Forces = Record<UnitType, number>;

/** A change to show in a preview or a report, e.g. { treasury: -4, wellbeing: 12 }. Missing keys mean 0. */
export type Delta = Partial<Record<Resource | IndexKey | 'support' | 'carbon' | 'resilience' | 'cohesion' | UnitType, number>>;

/* ------------------------------------------------------------------------------------------------ */
/* Phases and clock                                                                                 */
/* ------------------------------------------------------------------------------------------------ */

export type GamePhase = 'lobby' | 'quiz' | 'placement' | 'turn0' | 'turn' | 'ended';
/** Sub-phases of a numbered turn. */
export type TurnStage = 'briefing' | 'orders' | 'locked' | 'resolving';

export interface ClockState {
  /** Only meaningful while phase === 'turn'. */
  stage: TurnStage;
  /** Epoch ms when the current stage ends automatically, or null for "teacher advances". */
  deadline: number | null;
  /** True while the teacher has paused every screen. */
  paused: boolean;
  /** While paused: the ms that were left on the clock. */
  remainingMs: number | null;
  /** Default length of the orders stage in ms (about 18 minutes). */
  ordersMs: number;
  /** Default length of the briefing stage in ms; 0 means the teacher opens orders by hand. */
  briefingMs: number;
}

/* ------------------------------------------------------------------------------------------------ */
/* Roster                                                                                           */
/* ------------------------------------------------------------------------------------------------ */

/** Which line a student's placement card shows. It never says "your views match". */
export type PlacementCardLine = 'good_fit' | 'balance' | 'volunteer';

/** One student. Only first name + last initial is stored. PINs and tokens live in the Worker, not here. */
export interface RosterEntry {
  id: StudentId;
  /** "First L." */
  name: string;
  country: CountryId | null;
  roles: RoleId[];
  /** Attendance for the current turn (teacher toggle). Unanimity and two-minister rules count members present. */
  present: boolean;
  /** Absent on quiz day, opted out, or skipped most of the quiz. */
  flex: boolean;
  /** Seat order inside the team (used for Head of State rotation). */
  seat: number;
  hosVolunteer: boolean;
  card: PlacementCardLine | null;
}

/* ------------------------------------------------------------------------------------------------ */
/* Countries                                                                                        */
/* ------------------------------------------------------------------------------------------------ */

export type ConstructionItem =
  | { kind: 'unit'; unit: UnitType; count: number }
  | { kind: 'program'; program: ProgramId };

export interface QueuedBuild {
  item: ConstructionItem;
  /** Turn on which it becomes active. */
  due: number;
}

/** Timed effects (abilities, sanctions, tariffs, frozen factions, and so on). `until` is the last turn it applies. */
export type TimedEffect =
  | { kind: 'umbrella'; until: number; covered: CountryId[] }
  | { kind: 'summit'; until: number }
  | { kind: 'mediation'; until: number; a: CountryId; b: CountryId }
  | { kind: 'autonomy'; until: number; freeExitUsed: boolean }
  | { kind: 'factions_frozen'; until: number }
  | { kind: 'vision_decree'; due: number; until: number; half: boolean }
  | { kind: 'sanctioned'; until: number }
  | { kind: 'growth_paused'; until: number }
  | { kind: 'green_cooldown'; until: number }
  | { kind: 'tech_discount'; until: number };

export interface Tariff {
  target: CountryId;
  until: number;
  /** Karsova's standing Heritage Tariff (lifting it costs Public Support). */
  standing: boolean;
}

/** Counters used by objectives, awards, the Historians' Verdict and the debrief. All small integers. */
export interface CountryStats {
  tradesDone: number;
  tariffsImposed: number;
  embargoesImposed: number;
  warsDeclared: number;
  aggressions: number;
  attacksMade: number;
  zonesLost: number;
  capitalLost: boolean;
  /** Aid units given, per recipient. */
  aidGiven: Partial<Record<CountryId, number>>;
  aidTotal: number;
  /** Climate finance delivered by any route (Treasury units). */
  climateFinance: number;
  /** Sum of fair shares owed at meetings where the fund existed. */
  fairShareOwed: number;
  fairShareMetCount: number;
  fairShareMeetings: number;
  brokenPledges: number;
  brokenTreaties: number;
  energySoldToPartners: number;
  innovationActs: number;
  greenAbroad: number;
  shortfallTurnsFood: number;
  lastFoodShortfallTurn: number;
  lastEnergyShortfallTurn: number;
  foodNegativeEver: boolean;
  energyCoveredStreakFromT10: boolean;
  selfReliantStreak: number;
  bestSelfReliantStreak: number;
  refugeesAccepted: number;
  refugeeResponseDone: boolean;
  brokerCount: number;
  climateHitsSinceMeeting: number;
  climateDamageSinceMeeting: number;
  climateHitsTotal: number;
  assemblyWins: number;
  collapses: number;
  bankrupt: boolean;
  importedThisTurn: boolean;
  pandemicResponded: boolean;
  /** Climate finance delivered since the last fair-share check. */
  financeWindow: number;
  /** Green Transitions (of any kind) completed at home. */
  greenTransitions: number;
  missionsRun: number;
  spiesCaught: number;
  /** World Agenda vote pledges broken (counted against Most Trusted). Optional for worlds saved before agendas. */
  votePledgesBroken?: number;
  /** World Agendas this nation co-sponsored (at most 2 per game). */
  cosponsorships?: number;
  /** Assembly meetings at which an agenda vote already counted toward assemblyWins (at most 1 per meeting). */
  agendaWinsCounted?: number[];
}

/** Things that happened this turn (reset after each resolution). Used by factions and objectives. */
export interface TurnNotes {
  trades: number;
  aidGiven: number;
  greenCut: number;
  innovationAct: boolean;
  counterintel: boolean;
  leverageWin: boolean;
  humanRights: boolean;
  treatiesSigned: number;
  transparencyClauses: number;
  refugeesWelcomed: number;
  liftedStandingTariff: boolean;
  pledged: boolean;
  alliancesJoined: number;
  programsBuilt: number;
  diversified: boolean;
  attacked: boolean;
  wasAttacked: boolean;
  /** Trade partners already counted toward stats.tradesDone this turn (the trade filter). */
  tradePairs?: CountryId[];
}

/** Private per-turn trend point for the Country Ledger. */
export interface HistoryPoint {
  t: number;
  treasury: number;
  energy: number;
  food: number;
  support: number;
  prosperity: number;
  security: number;
  wellbeing: number;
  environment: number;
  carbon: number;
  index: number;
}

/** What a successful "gather" mission or an embassy reveals. */
export interface CoreSnapshot {
  treasury: number;
  energy: number;
  food: number;
  /** Null when the target is Kestran and no Transparency Clause covers the viewer (band is given instead). */
  support: number | null;
  supportBand: [number, number] | null;
  index: IndexValues;
  forces: Forces;
  carbon: number;
  resilience: number;
}

export interface IntelReport {
  turn: number;
  target: CountryId;
  snapshot: CoreSnapshot;
}

export interface CountryState {
  id: CountryId;
  slot: SlotId;
  isAI: boolean;
  temperament: Temperament;
  treasury: number;
  energy: number;
  food: number;
  /** Public Support 0-100 (not tradeable). */
  support: number;
  prosperity: number;
  security: number;
  wellbeing: number;
  environment: number;
  carbon: number;
  resilience: number;
  cohesion: number;
  /** Extra clean Energy production per turn (Energy Innovation Act, hydro gains, island). */
  cleanEnergy: number;
  /** Aurem's locked Sovereign Wealth Fund. 0 for everyone else. */
  lockedFund: number;
  fundUnlocked: boolean;
  /** Resources reserved by open trade proposals this country made. */
  escrow: ResourceBundle;
  forces: Forces;
  posture: Posture;
  queue: QueuedBuild[];
  programs: ProgramId[];
  diversification: number;
  adapt: { earlyWarning: boolean; heatSafe: boolean; mangrove: boolean };
  conservationUsed: number;
  forestCleared: number;
  lastFossilTurn: number;
  freeGreenUsed: boolean;
  abilityUsed: boolean;
  lastPlanTurn: number;
  royalGrants: boolean;
  effects: TimedEffect[];
  tariffs: Tariff[];
  embargoes: CountryId[];
  transparency: CountryId[];
  /** Nations this country has signed at least one treaty with (Kestran's opacity surcharge applies once each). */
  treatyPartnersEver: CountryId[];
  /** Prosperity at the last plan check (Ironvale's Tax-Cut Bet). */
  planProsperity: number;
  factionMood: number;
  factionLines: string[];
  /** Turn 0 values used by objectives and scoring. */
  start: {
    treasury: number;
    carbon: number;
    wellbeing: number;
    forcesTotal: number;
    /** Turn 0 force; upkeep is charged only on units above it. */
    forces: Forces;
    index: number;
  };
  objectives: [string, string];
  stats: CountryStats;
  history: HistoryPoint[];
  intel: IntelReport[];
  /** Island: developed by this nation. */
  islandDeveloped: boolean;
  notes: TurnNotes;
}

/* ------------------------------------------------------------------------------------------------ */
/* Map                                                                                              */
/* ------------------------------------------------------------------------------------------------ */

export interface Zone {
  id: ZoneId;
  /** Original owner, or null for the unowned T12 island. */
  home: CountryId | null;
  /** Current holder. */
  holder: CountryId | null;
  name: string;
  capital: boolean;
}

/* ------------------------------------------------------------------------------------------------ */
/* Turn orders                                                                                      */
/* ------------------------------------------------------------------------------------------------ */

export type Order =
  | { k: 'build'; role: RoleId; unit: UnitType; count: number }
  | { k: 'posture'; role: RoleId; posture: Posture }
  | { k: 'attack'; role: RoleId; target: CountryId; zone: ZoneId; army: number; navy: number; air: number }
  | { k: 'declare_war'; role: RoleId; target: CountryId }
  | { k: 'leave_treaty'; role: RoleId; treaty: TreatyId }
  | { k: 'mission'; role: RoleId; mission: MissionId; target: CountryId }
  | { k: 'program'; role: RoleId; program: ProgramId; energyPaid: number }
  | { k: 'climate'; role: RoleId; action: ClimateActionId; target?: CountryId }
  | { k: 'market'; role: RoleId; sellEnergy: number; sellFood: number; buyEnergy: number; buyFood: number }
  | { k: 'invest'; role: RoleId; area: InvestArea }
  | { k: 'aid'; role: RoleId; to: CountryId; treasury: number; energy: number; food: number; climate: boolean }
  | { k: 'tariff'; role: RoleId; target: CountryId; on: boolean }
  | { k: 'embargo'; role: RoleId; target: CountryId; on: boolean }
  | { k: 'respond_crisis'; role: RoleId; crisis: CrisisId }
  | { k: 'refugees'; role: RoleId; accept: number; fund: number }
  | { k: 'ability'; role: RoleId; target?: CountryId; target2?: CountryId; allies?: CountryId[]; shift?: ResourceBundle; fastTrack?: ProgramId }
  | { k: 'unlock_fund'; role: RoleId; amount: number }
  | { k: 'royal_grants'; role: RoleId; pay: boolean }
  | { k: 'develop_island'; role: RoleId }
  /** Arctic Opening: sail the new Arctic sea lane this turn (+1 Treasury). */
  | { k: 'arctic_transit'; role: RoleId }
  /** Joint Sea Patrol dues: pay in Treasury or lend Navy units for 3 turns. */
  | { k: 'seas_dues'; role: RoleId; choice: 'treasury' | 'navy' }
  /** Just Transition Compact: draw a share of the Transition Window at this Assembly. */
  | { k: 'window_draw'; role: RoleId };
export type OrderKind = Order['k'];

/** Repeatable once-per-turn investments. economy: +Prosperity; services: +Well-being; outreach: +Public Support. */
export type InvestArea = 'economy' | 'services' | 'outreach' | 'security';

export interface OrderSet {
  orders: Order[];
  /** Students who signed the current set. Changing orders clears signatures. */
  signatures: StudentId[];
  /** True when the signatures satisfy the ideology's decision rule. */
  signed: boolean;
  /** Roles whose desks marked "done" (cabinet readiness lights). */
  ready: RoleId[];
  /** Logged notes such as "Signed by the Defense Minister because the Head of State was absent." */
  notes: string[];
}

/* ------------------------------------------------------------------------------------------------ */
/* Diplomacy                                                                                        */
/* ------------------------------------------------------------------------------------------------ */

export type ProposalStatus = 'open' | 'accepted' | 'declined' | 'withdrawn' | 'countered' | 'expired';

/** A trade offer. `give` is reserved in the proposer's escrow until the offer closes. */
export interface TradeProposal {
  id: ProposalId;
  from: CountryId;
  to: CountryId;
  give: ResourceBundle;
  get: ResourceBundle;
  message: string;
  status: ProposalStatus;
  turn: number;
  counterOf: ProposalId | null;
  /** Filled when accepted: what each side actually received after tariff and trade-term rules. */
  settled: { fromGot: ResourceBundle; toGot: ResourceBundle } | null;
  /** A paid vote pledge offer: accepting this trade also pledges `to`'s vote to `from` on this agenda. */
  pledge?: { agenda: CountryId; choice: 'full' | 'amended' | 'no' } | null;
}

export interface TreatyProposal {
  id: ProposalId;
  from: CountryId;
  to: CountryId;
  type: TreatyType;
  terms: string;
  broker: CountryId | null;
  /** Kestran only: reveals its Public Support band to the partner (the Security Apparatus dislikes it). */
  transparency: boolean;
  status: ProposalStatus;
  turn: number;
  fromApprovals: StudentId[];
  toApprovals: StudentId[];
}

export interface Treaty {
  id: TreatyId;
  type: TreatyType;
  parties: [CountryId, CountryId];
  broker: CountryId | null;
  terms: string;
  signedTurn: number;
  /** Last turn the treaty applies, or null for open-ended. */
  until: number | null;
  active: boolean;
  endedTurn: number | null;
  endedBy: CountryId | null;
}

export interface War {
  /** The nation that declared. */
  a: CountryId;
  b: CountryId;
  startedTurn: number;
  /** True when the declarer had not been attacked by the target first (a war of aggression). */
  aggression: boolean;
  active: boolean;
  endedTurn: number | null;
  /** Turns in which each side attacked the other. */
  lastAttackTurn: Partial<Record<CountryId, number>>;
}

/* ------------------------------------------------------------------------------------------------ */
/* World Assembly and the Loss and Damage Fund                                                       */
/* ------------------------------------------------------------------------------------------------ */

export type VoteChoice = 'yes' | 'no' | 'abstain' | PayoutFormula | AgendaVersion;

/** The two versions of a World Agenda resolution. */
export type AgendaVersion = 'full' | 'amended';

export type AssemblyItemKind =
  | 'create_fund'
  | 'payout_formula'
  | 'censure'
  | 'sanctions'
  | 'ceasefire'
  | 'health_compact'
  | 'island_commons'
  | 'clean_tech_push'
  | 'coalition_item'
  | 'custom'
  /** A nation's World Agenda resolution (choices: full, amended, no, abstain). */
  | 'agenda';

export interface AssemblyItem {
  id: AssemblyItemId;
  kind: AssemblyItemKind;
  title: string;
  text: string;
  /** Climate items give the Atoll slot +1 vote (Island Alliance). */
  climate: boolean;
  target: CountryId | null;
  proposedBy: CountryId | null;
  choices: VoteChoice[];
  votes: Partial<Record<CountryId, VoteChoice>>;
  result: { passed: boolean; winner: VoteChoice; tally: Partial<Record<VoteChoice, number>> } | null;
  /** Present on 'agenda' items. `id` is the author nation (each nation has one agenda). */
  agenda?: { id: CountryId; reading: 'first' | 'second' | 'upgrade'; voteOnly: boolean };
  /** Views only: nations that have voted on an agenda item while the floor is still open (choices hidden). */
  votedBy?: CountryId[];
}

export interface Pledge {
  country: CountryId;
  amount: number;
  route: PledgeRoute;
  /** Turn at whose end the pledge is delivered automatically. */
  dueTurn: number;
  status: 'pending' | 'delivered' | 'broken' | 'cancelled';
}

export interface AssemblySession {
  turn: number;
  items: AssemblyItem[];
  resolved: boolean;
  /** Nations named in a critical motion at this session (at most one motion per nation). */
  named: CountryId[];
  /** The teacher closed the floor: agenda vote choices become visible (they are hidden until then). */
  floorClosed?: boolean;
  /** Times the teacher extended the floor by 2 minutes (the Worker runs the floor timer). */
  floorExtensions?: number;
}

export interface FundState {
  exists: boolean;
  createdTurn: number | null;
  balance: number;
  formula: PayoutFormula;
  pledges: Pledge[];
  totalPledged: number;
  totalPaidOut: number;
  /** Forest Payments are excluded from Save the Planet's finance count. */
  forestPaid: number;
  /** Turn after which the next payout happens (the turn after each meeting), or null. */
  payoutTurn: number | null;
  /** World-wide climate finance delivered (payouts + Clean Tech Transfers + adaptation aid). */
  worldFinance: number;
  /**
   * The part of `balance` that came from agenda dues, fees and missed-cut payments. Brief 12.2: that money
   * never counts as world finance, so payouts count only the voluntary share of the balance.
   */
  agendaMoney?: number;
}

/* ------------------------------------------------------------------------------------------------ */
/* World Agendas (docs/design-agendas.json, docs/BUILD_BRIEF.md section 12)                         */
/* ------------------------------------------------------------------------------------------------ */

export type AgendaStatus = 'draft' | 'seated' | 'passed_full' | 'passed_amended' | 'failed' | 'closed';
export type AgendaReading = 'first' | 'second' | 'upgrade';

/** Each nation has one World Agenda; its id is the author's CountryId. */
export interface AgendaState {
  id: CountryId;
  status: AgendaStatus;
  /** Meeting turn of the next vote on it (first reading, second reading or upgrade), or null. */
  seatTurn: number | null;
  /** What the next vote is. Second readings and upgrades are vote-only items. */
  seatKind: AgendaReading;
  passedTurn: number | null;
  version: AgendaVersion | null;
  /** Votes held so far. At most 2 per game (a first reading plus one second reading or upgrade). */
  readingsUsed: number;
  cosponsors: CountryId[];
  /** Open co-sponsor requests waiting for the author's answer. */
  cosponsorRequests: CountryId[];
  /** Paid pledge offers opened on this agenda, per proposing nation (capped at 3 each). */
  paidOffers: Partial<Record<CountryId, number>>;
  /** Every vote held on this agenda. */
  results: Array<{ turn: number; reading: AgendaReading; full: number; amended: number; no: number; abstain: number; outcome: AgendaVersion | 'failed' }>;
}

export type VotePledgeStatus = 'active' | 'kept' | 'broken' | 'withdrawn' | 'void';

/** Nation `from` privately promised nation `to` a vote on `agenda`. Only the two see it, unless it is broken. */
export interface VotePledge {
  id: string;
  from: CountryId;
  to: CountryId;
  agenda: CountryId;
  choice: 'full' | 'amended' | 'no';
  turn: number;
  /** True when linked to an escrowed trade (a paid pledge). */
  paid: boolean;
  tradeProposal: ProposalId | null;
  status: VotePledgeStatus;
}

/** One nation's duties under passed agendas (shown in the Our Nation panel). */
export interface AgendaObligation {
  kind: 'pact_cut' | 'seas_dues' | 'window_credit' | 'displacement_share' | 'no_expand' | 'tariff_ban' | 'fund_duty';
  agenda: CountryId;
  /** Turn by which it must be done, or the last turn it applies; null for the rest of the game. */
  due: number | null;
  amount: number;
  text: string;
}

/** Live effects of passed agendas and of the slot rules they answer. All small integers. */
export interface AgendaEffects {
  /** Delta Pact (Vellmark). */
  pact: {
    version: AgendaVersion | null;
    effectiveTurn: number;
    cuts: Array<{ country: CountryId; due: number; carbonAtStart: number; state: 'pending' | 'done' | 'missed'; votedFull: boolean; extended?: boolean }>;
    /** Pact Account: buys flood defenses for the hardest-hit vulnerable nation at each later Assembly. */
    account: number;
  };
  /** Joint Sea Patrol and Open Ports Accord (Kestran), and the Unsafe Seas slot rule. */
  seas: {
    version: AgendaVersion | null;
    effectiveTurn: number;
    raidProsperityLost: Partial<Record<CountryId, number>>;
    /** Navy units lent to the patrol and the last turn they stay assigned. */
    lent: Array<{ country: CountryId; units: number; until: number }>;
    duesDone: CountryId[];
  };
  /** Open Markets and Free Transit Charter (Meridia). */
  open: {
    version: AgendaVersion | null;
    effectiveTurn: number;
    /** The tariff and embargo ban lasts through this turn. */
    banUntil: number;
    fund: number;
    prosperityAtPassage: Partial<Record<CountryId, number>>;
    fundSettled: boolean;
  };
  /** Feed the World Pact (Ironvale). */
  food: { version: AgendaVersion | null; effectiveTurn: number; fundFood: number; fundTreasury: number; backstopUsed: CountryId[]; leverageBanUntil: number };
  /** Arctic Peace and Permafrost Treaty (Brightwater), and the Arctic Opening slot rule. */
  arctic: { version: AgendaVersion | null; effectiveTurn: number; transited: CountryId[]; securityLost: number };
  /** Just Transition Compact (Aurem). */
  window: {
    version: AgendaVersion | null;
    effectiveTurn: number;
    balance: number;
    credits: Array<{ country: CountryId; amount: number; expires: number }>;
    /** Drawers promised never to expand fossil fuels or clear forest. */
    noExpand: CountryId[];
  };
  /** Dignity in Displacement Compact (Alvera). */
  dignity: { version: AgendaVersion | null; effectiveTurn: number; shares: Partial<Record<CountryId, number>> };
  /** Right to Stay Compact (Karsova). */
  stay: { version: AgendaVersion | null; effectiveTurn: number; fund: number; payers: CountryId[]; karsovaBanUntil: number };
}

/* ------------------------------------------------------------------------------------------------ */
/* Climate                                                                                          */
/* ------------------------------------------------------------------------------------------------ */

export type HeatStage = 1 | 2 | 3 | 4;

export interface HeatState {
  /** x8 integer scale. Display = meter / 8 with one decimal. */
  meter: number;
  stage: HeatStage;
  /** Turn each stage was first entered (index = stage). */
  stageTurns: Partial<Record<HeatStage, number>>;
  /** World total Carbon at Turn 0 (for the clean-tech tipping point). */
  carbon0Total: number;
  cleanTechTurn: number | null;
  methaneFired: number;
  diebackFired: boolean;
  /** Pending discount for the next Green Transition by any nation (Meridia's Innovation Prize). */
  innovationPrizes: number;
  /** The Clean Technology Push passed: Green Transition costs 1 less for everyone. */
  cleanTechPush: boolean;
  lastAdd: number;
}

/* ------------------------------------------------------------------------------------------------ */
/* Crises                                                                                           */
/* ------------------------------------------------------------------------------------------------ */

export interface CrisisState {
  id: CrisisId;
  /** Turn on which it strikes (announced at that turn's briefing, resolved at its end). */
  turn: number;
  status: 'scheduled' | 'active' | 'resolved' | 'cancelled';
  /** Nations that funded a response this turn. */
  responders: CountryId[];
}

export interface IslandState {
  discovered: boolean;
  developer: CountryId | null;
  protected: boolean;
}

/* ------------------------------------------------------------------------------------------------ */
/* News and events                                                                                  */
/* ------------------------------------------------------------------------------------------------ */

export type NewsKind = 'headline' | 'rumor' | 'faction' | 'report' | 'assembly' | 'crisis' | 'climate';
export type NewsSpot = 'defense' | 'economy' | 'science' | 'trade' | 'treaty' | 'vote';

export interface NewsItem {
  id: NewsId;
  turn: number;
  kind: NewsKind;
  spot: NewsSpot | null;
  /** Image id from src/client/generated/images.json, or null (the T18 attack card is icon-only). */
  image: string | null;
  icon: string | null;
  title: string;
  body: string;
  /** 'all', or the one country allowed to see it. */
  audience: 'all' | CountryId;
  countries: CountryId[];
  /** Teacher-only: whether a rumor is true. Stripped from student and projector views. */
  rumorTrue: boolean | null;
}

export type EventKind =
  | 'income'
  | 'upkeep'
  | 'shortfall'
  | 'market'
  | 'built'
  | 'program'
  | 'invest'
  | 'climate_action'
  | 'climate_hit'
  | 'climate_gain'
  | 'stage'
  | 'tipping'
  | 'meter'
  | 'crisis'
  | 'crisis_hit'
  | 'crisis_response'
  | 'war'
  | 'battle'
  | 'capture'
  | 'peace'
  | 'mission'
  | 'spy_caught'
  | 'trade'
  | 'treaty'
  | 'treaty_left'
  | 'aid'
  | 'tariff'
  | 'embargo'
  | 'assembly'
  | 'pledge'
  | 'pledge_broken'
  | 'payout'
  | 'factions'
  | 'unrest'
  | 'ability'
  | 'refugees'
  | 'plan_check'
  | 'credit'
  | 'island'
  | 'collapse'
  | 'rule'
  | 'order_failed'
  | 'phase'
  | 'game_over'
  | 'agenda_seated'
  | 'agenda_result'
  | 'agenda_effect'
  | 'raid';

/**
 * One thing that happened. `vis` controls who may see it: 'public' (everyone), 'private' (only `c` and,
 * when listed, `o`), 'teacher' (teacher only). `n` holds small numbers; `s` is a short plain-language line.
 */
export interface GameEvent {
  t: number;
  k: EventKind;
  vis: 'public' | 'private' | 'teacher';
  c?: CountryId;
  o?: CountryId;
  n?: Record<string, number>;
  s: string;
}

/* ------------------------------------------------------------------------------------------------ */
/* Scoring                                                                                          */
/* ------------------------------------------------------------------------------------------------ */

export type GlobalGoalId = 'global_peace' | 'end_famine' | 'contain_pandemic' | 'save_planet';
export type AwardId =
  | 'most_prosperous'
  | 'most_secure'
  | 'healthiest'
  | 'greenest'
  | 'most_trusted'
  | 'most_influential'
  | 'climate_justice';

export interface CountryScore {
  country: CountryId;
  index: number;
  par: number;
  indexPoints: number;
  goalPoints: number;
  awards: AwardId[];
  awardPoints: number;
  objectivesMet: [boolean, boolean];
  objectivePoints: number;
  verdict: boolean;
  verdictPoints: number;
  /** World Agenda points (author and co-sponsor). Already included in total. */
  agendaPoints?: number;
  total: number;
}

export interface FinalResults {
  turn: number;
  goals: Record<GlobalGoalId, boolean>;
  scores: CountryScore[];
  meter: number;
  worldFinance: number;
  cooperated: boolean;
}

/* ------------------------------------------------------------------------------------------------ */
/* World                                                                                            */
/* ------------------------------------------------------------------------------------------------ */

/** 'classic' is the default (the World Agenda design keeps it); 'crossroads' is the lint-clean teacher alternative. */
export type PairingMode = 'classic' | 'crossroads';
export type BotSkill = 'novice' | 'strong';
export type ClimatePolicy = 'doctrine' | 'bau' | 'moderate' | 'aggressive';

export interface WorldConfig {
  /** 6, 7 or 8 nations. */
  nNations: 6 | 7 | 8;
  pairing: PairingMode;
  /** Scheduled final turn (never shown to students). The teacher may end earlier. */
  maxTurns: number;
  assemblyTurns: number[];
  /**
   * Reserved, no effect: strategic weapons are not part of this version of Sovereign. createWorld always sets it
   * to false and no rule reads it (the handbook and the teacher's create-world form say weapons are off).
   */
  strategicWeapons: boolean;
  /** Execute filed but unsigned orders at the deadline (logged). */
  autoSignAtDeadline: boolean;
  botSkill: BotSkill;
  climatePolicy: ClimatePolicy;
  /** World Agendas on (default true). The simulator can switch them off for A/B runs. */
  agendas?: boolean;
  /** The slot rules the agendas answer (Unsafe Seas, Arctic Opening). Default true. */
  slotRules?: boolean;
  /** Show the real-world "inspired by" labels to students and the projector (default false). */
  revealLabels?: boolean;
  /** Teacher-chosen student family labels (from each ideology's labelOptions). */
  labelChoices?: Partial<Record<CountryId, string>>;
  /** Simulator only: bots in these seats play as novices and skip 15% of agenda votes (Core Four proxy). */
  proxyHumanSeats?: CountryId[];
  /** Simulator only: this bot author buys votes at any price (the "buy every vote" exploit test). */
  exploitAuthor?: CountryId;
}

/** World-level flags that outlive a turn. */
export interface WorldFlags {
  pandemicContained: boolean | null;
  healthCompact: boolean;
  /** Attacks are paused through this turn (Assembly ceasefire). */
  truceUntil: number;
  lastAttackTurn: number;
  /** Brightwater's Grand Coalition adds an item to the next session. */
  coalitionItemPending: boolean;
  /** Nations that pledged at the most recent session (Karsova's go-it-alone rule). */
  pledgedLastSession: CountryId[];
  /** No new tariffs or embargoes through this turn (Open Markets Charter). */
  tariffBanUntil?: number;
  /** Raids from the Unsafe Seas rule have ended (Joint Sea Patrol). */
  seasSafe?: boolean;
  arcticTreaty?: 'none' | 'full' | 'amended';
  /** The Full Delta Pact is in force (clean-tech tipping safeguard). */
  pactFullActive?: boolean;
  /** Teacher-set order of first readings per meeting turn (World Agendas docket). */
  docketOrder?: Partial<Record<number, CountryId[]>>;
}

export interface WorldCounters {
  proposal: number;
  treaty: number;
  news: number;
  item: number;
}

export interface WorldState {
  /** Schema version of this JSON. */
  v: 1;
  id: string;
  className: string;
  createdAt: number;
  seed: number;
  /** Current seeded RNG state (uint32). */
  rng: number;
  config: WorldConfig;
  phase: GamePhase;
  /** 0 before and during Turn 0; 1..N during play. */
  turn: number;
  clock: ClockState;
  /** Set when the teacher ends the game: the current turn becomes the final one. */
  endRequested: boolean;
  roster: RosterEntry[];
  /** Nations in this world, in display order. */
  nations: CountryId[];
  countries: Partial<Record<CountryId, CountryState>>;
  zones: Zone[];
  heat: HeatState;
  fund: FundState;
  orders: Partial<Record<CountryId, OrderSet>>;
  trades: TradeProposal[];
  treatyProposals: TreatyProposal[];
  treaties: Treaty[];
  wars: War[];
  assembly: AssemblySession | null;
  assemblyHistory: AssemblySession[];
  crises: CrisisState[];
  island: IslandState;
  news: NewsItem[];
  /** Events from the most recent resolution (for the night replay). */
  lastEvents: GameEvent[];
  counters: WorldCounters;
  flags: WorldFlags;
  final: FinalResults | null;
  /** World Agendas by author. Optional so worlds saved before agendas still load (the engine fills them in). */
  agendas?: Partial<Record<CountryId, AgendaState>>;
  votePledges?: VotePledge[];
  agendaFx?: AgendaEffects;
}

/* ------------------------------------------------------------------------------------------------ */
/* Instant actions (applied immediately, not at turn resolution)                                    */
/* ------------------------------------------------------------------------------------------------ */

export type InstantAction =
  | { k: 'propose_trade'; to: CountryId; give: ResourceBundle; get: ResourceBundle; message?: string; pledge?: { agenda: CountryId; choice: 'full' | 'amended' | 'no' } }
  | { k: 'accept_trade'; proposal: ProposalId }
  | { k: 'decline_trade'; proposal: ProposalId }
  | { k: 'withdraw_trade'; proposal: ProposalId }
  | { k: 'counter_trade'; proposal: ProposalId; give: ResourceBundle; get: ResourceBundle; message?: string }
  | { k: 'propose_treaty'; to: CountryId; type: TreatyType; terms?: string; broker?: CountryId | null; transparency?: boolean }
  | { k: 'accept_treaty'; proposal: ProposalId }
  | { k: 'ratify_treaty'; proposal: ProposalId }
  | { k: 'decline_treaty'; proposal: ProposalId }
  | { k: 'withdraw_treaty'; proposal: ProposalId }
  | { k: 'vote'; item: AssemblyItemId; choice: VoteChoice }
  | { k: 'pledge'; amount: number; route: PledgeRoute }
  | { k: 'motion'; kind: 'censure' | 'sanctions' | 'ceasefire' | 'custom'; target?: CountryId | null; text?: string }
  | { k: 'file_orders'; role: RoleId; orders: Order[] }
  | { k: 'sign_orders' }
  | { k: 'set_ready'; role: RoleId; ready: boolean }
  /** Author: seat (or move) our World Agenda at a later meeting. File by the lock of turn M-2. */
  | { k: 'table_agenda'; turn: number }
  /** Ask to co-sponsor another nation's agenda (at most 2 per game). */
  | { k: 'cosponsor_request'; agenda: CountryId }
  /** Author: accept or decline a co-sponsor request (up to 3 co-sponsors). */
  | { k: 'cosponsor_answer'; agenda: CountryId; country: CountryId; accept: boolean }
  /** Privately promise nation `to` a vote. With `tradeProposal`, also accepts that paid offer from `to`. */
  | { k: 'pledge_vote'; agenda: CountryId; to: CountryId; choice: 'full' | 'amended' | 'no'; tradeProposal?: ProposalId }
  /** Withdraw a vote pledge you made, before pledges close (a withdrawn pledge is not broken). */
  | { k: 'withdraw_pledge'; pledge: string };
export type InstantActionKind = InstantAction['k'];

/** Who is acting. The Worker builds this from the verified token; the engine looks up roles itself. */
export type Actor =
  | { kind: 'student'; studentId: StudentId }
  | { kind: 'ai'; country: CountryId }
  | { kind: 'teacher' };

/** Teacher console commands. */
export type TeacherCommand =
  | { k: 'start_quiz' }
  | { k: 'open_placement' }
  | { k: 'publish_placement'; publication: PlacementPublication }
  | { k: 'start_game' }
  | { k: 'open_orders'; ms?: number }
  | { k: 'pause' }
  | { k: 'resume' }
  | { k: 'extend'; ms: number }
  | { k: 'lock_now' }
  | { k: 'resolve_now' }
  | { k: 'end_game' }
  | { k: 'set_timing'; ordersMs?: number; briefingMs?: number }
  | { k: 'set_attendance'; student: StudentId; present: boolean }
  | { k: 'set_roles'; student: StudentId; roles: RoleId[] }
  | { k: 'move_student'; student: StudentId; country: CountryId }
  | { k: 'adjust'; country: CountryId; delta: Delta }
  | { k: 'trigger_crisis'; crisis: CrisisId }
  | { k: 'delay_crisis'; crisis: CrisisId; turns: number }
  | { k: 'close_assembly' }
  | { k: 'add_students'; names: string[] }
  /** Agenda docket controls. `order` lists the first readings of that meeting in the new order. */
  | { k: 'reorder_docket'; turn: number; order: CountryId[] }
  | { k: 'bump_agenda'; agenda: CountryId }
  | { k: 'close_floor' }
  | { k: 'extend_floor' }
  /** Reveal (or hide again) all eight real-world inspirations to students and the projector (logged). */
  | { k: 'reveal_inspirations'; on: boolean }
  /** Choose a student family label from the country's labelOptions. */
  | { k: 'set_label'; country: CountryId; label: string };
export type TeacherCommandKind = TeacherCommand['k'];

export type ActionErrorCode =
  | 'wrong_phase'
  | 'paused'
  | 'not_on_team'
  | 'not_your_role'
  | 'invalid'
  | 'not_found'
  | 'not_allowed'
  | 'insufficient'
  | 'limit'
  | 'already_used'
  | 'at_war'
  | 'treaty_blocks'
  | 'closed';

export interface ActionError {
  code: ActionErrorCode;
  /** Plain words for the student, e.g. "You need 3 Treasury. You have 2 free." */
  message: string;
}

/* ------------------------------------------------------------------------------------------------ */
/* Placement (runs in the teacher's browser)                                                         */
/* ------------------------------------------------------------------------------------------------ */

export type PlacementMode = 'core_four' | 'best_fit' | 'perspective' | 'random';
export type StudentMode = 'match' | 'stretch' | 'surprise';
export type RankLabel = '#1' | '#2' | '#3' | 'other' | 'flex' | 'volunteer';

/** One student's input to placement, after the teacher's browser decrypts the envelope in memory. */
export interface PlacementStudent {
  id: StudentId;
  /** Raw quiz vector; null axes are allowed. Null overall means flex (absent, opted out). */
  vector: Record<Axis, number | null> | null;
  priorityAxis: Axis | null;
  mode: StudentMode;
  openToDifferent: boolean;
  declined: CountryId[];
  hosVolunteer: boolean;
  /** Optional teacher tags held only in browser memory. */
  experienced?: boolean;
  /** Review markers from quiz flags (speeding, attention check, straight-lining, hash match). */
  markers?: string[];
}

export interface PlacementTeacherInputs {
  pins?: CountryId[];
  locks?: Partial<Record<StudentId, CountryId>>;
  forbids?: Array<{ student: StudentId; country: CountryId }>;
  keepApart?: Array<[StudentId, StudentId]>;
  keepTogether?: Array<[StudentId, StudentId]>;
  nTeamsOverride?: number;
}

export interface PlacementAssignment {
  student: StudentId;
  country: CountryId;
  rank: RankLabel;
  card: PlacementCardLine;
  roles: RoleId[];
  markers: string[];
}

export interface PlacementFigures {
  pctFirstActive: number;
  pctTopTwoActive: number;
  pctNearestAiOnly: number;
  pctOffProfile: number;
}

export interface PlacementResult {
  seed: number;
  nTeams: number;
  human: CountryId[];
  /** The AI nations, lowest total class fit first (the order in which the world drops pairings when it shrinks). */
  ai: CountryId[];
  assignments: PlacementAssignment[];
  figures: PlacementFigures;
  notices: string[];
  /** The teacher's script line, generated from the real numbers. */
  scriptLine: string;
  cost: number;
}

/** What the teacher publishes to the Durable Object. Contains no quiz data. */
export interface PlacementPublication {
  human: CountryId[];
  /**
   * The AI nations, lowest total class fit first (PlacementResult.ai). When the teacher shrinks the world, whole AI
   * pairings are dropped in this order where the world's balance rules allow. Contains no quiz data.
   */
  aiByFit?: CountryId[];
  assignments: Array<{ student: StudentId; country: CountryId; roles: RoleId[]; card: PlacementCardLine }>;
  scriptLine: string;
}

/* ------------------------------------------------------------------------------------------------ */
/* Quiz (scored in the student's browser)                                                            */
/* ------------------------------------------------------------------------------------------------ */

/** Likert value: -1, -0.5, 0, 0.5, 1, or null for "Prefer not to answer". */
export type LikertValue = -1 | -0.5 | 0 | 0.5 | 1 | null;

export type QuizAnswer =
  | { item: string; kind: 'likert'; value: LikertValue; ms: number }
  | { item: string; kind: 'choice'; option: string | null; ms: number }
  | { item: string; kind: 'priority'; axis: Axis | null; ms: number };

export interface QuizFlags {
  speeding: boolean;
  attentionFailed: boolean;
  straightLining: boolean;
}

/** The plaintext that the student's browser encrypts. Never stored in plaintext anywhere. */
export interface QuizPlaintext {
  v: 1;
  vector: Record<Axis, number | null>;
  priorityAxis: Axis | null;
  flags: QuizFlags;
  /** Salted hash of the ordered answer string (hex), for the collusion marker. */
  answerHash: string;
  /** The student's placement choices. Sealed with the answers, so declines never reach the server in clear. */
  choices?: QuizChoices;
}

/** The student's placement choices (sealed inside the quiz envelope). */
export interface QuizChoices {
  mode: StudentMode;
  openToDifferent: boolean;
  declined: CountryId[];
  hosVolunteer: boolean;
}

/** ECDH P-256 + AES-GCM envelope. Only this ciphertext reaches the server. */
export interface QuizEnvelope {
  v: 1;
  alg: 'ECDH-P256+A256GCM';
  /** Ephemeral public key of the student's browser (JWK, P-256). */
  epk: JsonWebKeyLike;
  /** Base64 12-byte IV. */
  iv: string;
  /** Base64 ciphertext of JSON.stringify(QuizPlaintext). */
  ct: string;
}

/** Minimal JWK shape so this file does not depend on DOM lib types. */
export interface JsonWebKeyLike {
  kty: string;
  crv?: string;
  x?: string;
  y?: string;
  d?: string;
  ext?: boolean;
  key_ops?: string[];
}

/** What a student sees right after submitting: in-world words only. */
export interface RoleSuggestion {
  country: CountryId;
  title: string;
  line: string;
}

/* ------------------------------------------------------------------------------------------------ */
/* Chat                                                                                             */
/* ------------------------------------------------------------------------------------------------ */

/**
 * Chat channels:
 * - 'world': everyone; 'assembly': the World Assembly floor (Foreign Ministers and Heads of State post)
 * - `team:${CountryId}`: one cabinet
 * - `pair:${CountryId}:${CountryId}`: two nations (ids sorted alphabetically)
 * - `alliance:${TreatyId}`: members of an alliance treaty
 */
export type ChannelId = 'world' | 'assembly' | `team:${string}` | `pair:${string}:${string}` | `alliance:${string}`;

export interface ChatMessage {
  id: number;
  ch: ChannelId;
  from: { kind: 'student' | 'teacher' | 'ai' | 'system'; id: string; name: string; country: CountryId | null };
  text: string;
  at: number;
  flagged: boolean;
}

/* ------------------------------------------------------------------------------------------------ */
/* Client views (built per viewer by the engine's buildView)                                         */
/* ------------------------------------------------------------------------------------------------ */

export type Viewer = { kind: 'student'; studentId: StudentId } | { kind: 'teacher' } | { kind: 'projector' };

export interface PublicCountry {
  id: CountryId;
  name: string;
  shortName: string;
  slot: SlotId;
  slotName: string;
  isAI: boolean;
  /** Carbon rating 0-5 and historical share are always public. */
  carbon: number;
  historicalShare: number;
  mostVulnerable: boolean;
  fairShare: number;
  zonesHeld: ZoneId[];
  atWarWith: CountryId[];
  brokenPledges: number;
  /** In-world one-liner (never the real-world label). */
  oneLiner: string;
  accent: string;
  pattern: string;
  par: number;
  /** Student-facing ideology family label, for example "Social Democracy". */
  familyLabel?: string;
  /** Real-world inspiration: students and projector see it only after the teacher's reveal or at the debrief. */
  inspiredBy?: string;
}

export interface PublicTreaty {
  id: TreatyId;
  type: TreatyType;
  parties: [CountryId, CountryId];
  broker: CountryId | null;
  terms: string;
  signedTurn: number;
  until: number | null;
  active: boolean;
}

export interface PublicWar {
  a: CountryId;
  b: CountryId;
  startedTurn: number;
  active: boolean;
}

export interface HeatView {
  meter: number;
  display: string;
  stage: HeatStage;
  stageName: string;
  /** 520 minus the meter, as display units (one decimal). */
  budgetLeft: string;
  /** Turns left before Danger at the current rate, or null if the meter is not rising. */
  turnsToDanger: number | null;
  lastAdd: number;
  cleanTech: boolean;
}

export interface FundView {
  exists: boolean;
  balance: number;
  formula: PayoutFormula;
  pledges: Pledge[];
  totalPaidOut: number;
  worldFinance: number;
}

export interface AssemblyView {
  /** The current or upcoming session, or null. */
  session: AssemblySession | null;
  nextTurn: number | null;
  history: Array<{ turn: number; items: Array<{ title: string; passed: boolean; winner: VoteChoice }> }>;
}

export interface CrisisView {
  id: CrisisId;
  title: string;
  body: string;
  image: string | null;
  icon: string;
  turn: number;
  status: CrisisState['status'];
  responseCost: number;
}

export interface PublicWorld {
  worldId: string;
  className: string;
  phase: GamePhase;
  turn: number;
  clock: ClockState;
  serverTime: number;
  heat: HeatView;
  fund: FundView;
  countries: PublicCountry[];
  zones: Zone[];
  treaties: PublicTreaty[];
  wars: PublicWar[];
  assembly: AssemblyView;
  crisis: CrisisView | null;
  island: IslandState;
  news: NewsItem[];
  replay: GameEvent[];
  final: FinalResults | null;
  /** Every nation's World Agenda. Public: text, author, status, docket seat, results, co-sponsors. */
  agendas?: PublicAgendaView[];
  /** Broken vote pledges are public. */
  brokenPledges?: Array<{ from: CountryId; to: CountryId; agenda: CountryId; turn: number }>;
}

/** Public card for one World Agenda. */
export interface PublicAgendaView {
  id: CountryId;
  author: CountryId;
  authorName: string;
  familyLabel: string;
  title: string;
  /** The three lead lines of an agenda card. */
  lead: { does: string; costs: string; votes: string };
  fullText: string;
  amendedTitle: string;
  amendedText: string;
  reservedTurn: number;
  status: AgendaStatus;
  seatTurn: number | null;
  seatKind: AgendaReading;
  version: AgendaVersion | null;
  passedTurn: number | null;
  cosponsors: CountryId[];
  thresholds: { full: number; amended: number };
  results: AgendaState['results'];
  /** Leaning for or against by beliefs only. Material costs are in whoPays and the preview. */
  leaningFor: CountryId[];
  leaningAgainst: CountryId[];
  whoPays: string;
}

/** Engine-computed preview of an agenda for one nation, in game units. Bots use the same numbers. */
export interface AgendaPreview {
  agenda: CountryId;
  version: AgendaVersion;
  /** One-time change at passage. */
  now: Delta;
  /** Average change per turn from next turn to the end of the game. */
  perTurn: Delta;
  /** Projected turn each Heat stage begins, without and with the agenda (a projection, never a promise). */
  projectedStageTurns: { without: Partial<Record<HeatStage, number>>; with: Partial<Record<HeatStage, number>> };
  meterUnitsSaved: number;
  /** Total value in game units: Treasury + Food + Energy/2 + index points/2 + Public Support/4, capped at +/-9. */
  impact: number;
  notes: string[];
}

/** The student's own nation profile (the Our Nation panel and the Turn 0 country briefing). */
export interface NationPanelView {
  familyLabel: string;
  summary: string;
  beliefs: string[];
  coreValues: string;
  government: string;
  decisionRule: string;
  strengths: string[];
  weaknesses: string[];
  ability: { name: string; text: string; status: 'ready' | 'used' | 'later'; readyTurn: number | null };
  /** Only after the teacher's reveal or at the debrief. */
  inspiredBy?: string;
  land: { slotName: string; terrain: string; topHazard: string; geographyNote: string; advantages: string[] };
  /** "How hard the world's problems hit us": bars for all nations, ours ranked. */
  exposure: Array<{ label: string; bars: Array<{ country: CountryId; value: number }>; rank: number }>;
  agenda: {
    id: CountryId;
    title: string;
    fullText: string;
    amendedTitle: string;
    amendedText: string;
    whyUs: string;
    dilemma: string;
    whoPays: string;
    chips: string;
    speech: string;
    status: AgendaStatus;
    /** Plain words for the status stepper, e.g. "Seated for Turn 5". */
    stepper: string;
    seatTurn: number | null;
    reservedTurn: number;
    votesNeeded: { full: number; amended: number };
    /** Pledges we hold on our agenda (including our own vote). */
    whip: { full: number; amended: number; no: number; unknown: number };
    cosponsors: CountryId[];
    cosponsorRequests: CountryId[];
    leaningFor: CountryId[];
    leaningAgainst: CountryId[];
    naturalAllies: CountryId[];
    naturalOpponents: CountryId[];
    previewFull: AgendaPreview;
    previewAmended: AgendaPreview;
    paidOffersUsed: number;
    paidOffersMax: number;
  };
  pledges: VotePledge[];
  obligations: AgendaObligation[];
  objectives: ObjectiveView[];
  factionMood: number;
  /** False when World Agendas are off in this world: the agenda part is then empty and should be hidden. */
  agendasOn?: boolean;
}

/** Another nation as seen by a student: public data, plus core numbers only with an embassy or fresh intel. */
export interface OtherCountryView extends PublicCountry {
  embassy: boolean;
  core: CoreSnapshot | null;
  /** Turn of the intel report that `core` came from, when it came from intelligence. */
  coreFromIntelTurn: number | null;
  relation: 'ally' | 'partner' | 'neutral' | 'war';
}

export interface ProjectionLine {
  label: string;
  delta: Delta;
}

export interface PreviewResult {
  /** Totals of the filed orders, e.g. { treasury: -4, wellbeing: 12 }. */
  delta: Delta;
  /** Per-order lines in the same order as the input. */
  lines: ProjectionLine[];
  /** Expected balances at the end of the turn (orders + income + upkeep, before crises and dice). */
  projected: ResourceBundle;
  income: ResourceBundle;
  upkeep: ResourceBundle;
  /** Chances for attacks and missions (0-100). */
  odds: Array<{ label: string; percent: number }>;
  warnings: string[];
  errors: Array<{ index: number; error: ActionError }>;
}

export interface OwnCountryView {
  id: CountryId;
  name: string;
  slot: SlotId;
  slotName: string;
  landscape: string;
  landscapeStressed: string;
  treasury: number;
  energy: number;
  food: number;
  escrow: ResourceBundle;
  available: ResourceBundle;
  /** Null for Kestran's own team (it sees only the band). */
  support: number | null;
  supportBand: [number, number] | null;
  index: IndexValues;
  nationalIndex: number;
  carbon: number;
  resilience: number;
  cohesion: number;
  lockedFund: number;
  forces: Forces;
  posture: Posture;
  queue: QueuedBuild[];
  programs: ProgramId[];
  diversification: number;
  adapt: CountryState['adapt'];
  abilityUsed: boolean;
  abilityReady: boolean;
  effects: TimedEffect[];
  tariffs: Tariff[];
  embargoes: CountryId[];
  factionMood: number;
  factionLines: string[];
  history: HistoryPoint[];
  intel: IntelReport[];
  par: number;
  fairShare: number;
  royalGrants: boolean;
  stats: Pick<CountryStats, 'tradesDone' | 'aidTotal' | 'climateFinance' | 'brokenPledges'>;
  /** The Our Nation panel: ideology, World Agenda with dilemma and live status, objectives, obligations. */
  nation?: NationPanelView;
  /** The same profile when World Agendas are off (then `nation` is absent and `nationProfile.agendasOn` is false). */
  nationProfile?: NationPanelView;
}

export interface CardView {
  country: CountryId;
  name: string;
  oneLiner: string;
  government: string;
  strengths: string[];
  weaknesses: string[];
  ability: { name: string; text: string };
  climateLever: string;
  decisionRule: string;
  factions: Array<{ name: string; wants: string }>;
  steelman: SteelmanCard;
  par: number;
  familyLabel?: string;
  agenda?: { id: CountryId; title: string; reservedTurn: number };
}

/** Same template for all eight countries. The lens is shown at the debrief only. */
export interface SteelmanCard {
  inOwnWords: string;
  strengths: string[];
  critics: string[];
  lens: { affirms: string[]; challenges: string[] } | null;
}

export interface ObjectiveView {
  id: string;
  title: string;
  text: string;
  /** Progress hint in plain words, e.g. "5 of 8 trade deals done". */
  progress: string;
  /** Numeric progress for a progress bar: value of max. */
  value?: number;
  max?: number;
}

export interface BriefingLine {
  role: RoleId;
  text: string;
  tone: 'info' | 'good' | 'warn';
}

export interface Teammate {
  studentId: StudentId;
  name: string;
  roles: RoleId[];
  present: boolean;
}

export interface StudentView {
  kind: 'student';
  me: { studentId: StudentId; name: string; roles: RoleId[]; country: CountryId | null; card: PlacementCardLine | null };
  world: PublicWorld;
  /** Null until placement is published. */
  country: OwnCountryView | null;
  card: CardView | null;
  others: OtherCountryView[];
  teammates: Teammate[];
  orders: OrderSet | null;
  preview: PreviewResult | null;
  trades: TradeProposal[];
  treatyProposals: TreatyProposal[];
  objectives: ObjectiveView[];
  briefing: BriefingLine[];
  priorities: string[];
  channels: ChannelId[];
  /** Our vote pledges (made and received). */
  votePledges?: VotePledge[];
}

export interface TeacherCountryView {
  country: CountryState;
  inspiredBy: string;
  familyLabel?: string;
  labelOptions?: string[];
  orders: OrderSet | null;
  score: CountryScore;
}

export interface TeacherView {
  kind: 'teacher';
  world: PublicWorld;
  roster: RosterEntry[];
  countries: TeacherCountryView[];
  trades: TradeProposal[];
  treatyProposals: TreatyProposal[];
  events: GameEvent[];
  config: WorldConfig;
  seed: number;
  /** Full agenda state, every vote pledge and the live effects (teacher only). */
  agendas?: Partial<Record<CountryId, AgendaState>>;
  votePledges?: VotePledge[];
  agendaFx?: AgendaEffects;
  /** Whip counts per agenda across all nations (pledged Full, Amended, No). */
  whip?: Partial<Record<CountryId, { full: number; amended: number; no: number }>>;
}

export interface ProjectorView {
  kind: 'projector';
  world: PublicWorld;
  /** Cabinet readiness per human nation (count of ready desks), no order content. */
  readiness: Array<{ country: CountryId; ready: number; roles: number; signed: boolean }>;
}

export type ClientView = StudentView | TeacherView | ProjectorView;
